import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { AlertTriangle, History, Clock, Users, Package, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { API_URL, API_HOST } from '../config/api';
import { withApiToken } from '../utils/apiAuth';
import logService from '../services/logService';
import useEscapeClose from '../hooks/useEscapeClose';
import './InventoryCount.css';

// Ses calma fonksiyonlari (localStorage'dan ayar kontrolü)
const playSound = (type) => {
  // Ses ayarını kontrol et - kapalıysa ses çalma
  try {
    const soundEnabled = localStorage.getItem('soundEnabled');
    if (soundEnabled === 'false') {
      return;
    }
  } catch (e) {
    // localStorage erişim hatası - devam et
  }

  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    if (type === 'success') {
      oscillator.frequency.value = 1200;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.3;
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.1);
    } else {
      oscillator.frequency.value = 400;
      oscillator.type = 'square';
      gainNode.gain.value = 0.2;
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.15);

      setTimeout(() => {
        const osc2 = audioContext.createOscillator();
        const gain2 = audioContext.createGain();
        osc2.connect(gain2);
        gain2.connect(audioContext.destination);
        osc2.frequency.value = 300;
        osc2.type = 'square';
        gain2.gain.value = 0.2;
        osc2.start();
        osc2.stop(audioContext.currentTime + 0.15);
      }, 180);
    }
  } catch (error) {
    console.error('Ses çalma hatası:', error);
  }
};

const InventoryCount = ({ user, selectedStore }) => {
  const barcodeInputRef = useRef(null);
  const isSSEUpdate = useRef(false); // SSE'den gelen güncelleme mi?

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inventoryData, setInventoryData] = useState({});
  const [barcodeInput, setBarcodeInput] = useState('');
  const [lastScanned, setLastScanned] = useState(null);
  const [filterMode, setFilterMode] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedMetalType, setSelectedMetalType] = useState('all');

  // Sayim oturumu state'leri
  const [session, setSession] = useState(null);
  const [showTerminalModal, setShowTerminalModal] = useState(false);
  const [terminals, setTerminals] = useState([]);
  const [loadingTerminals, setLoadingTerminals] = useState(false);

  // Fazla ve özet state'leri
  const [excessAdditions, setExcessAdditions] = useState({});
  const [showExcessModal, setShowExcessModal] = useState(false);
  const [pendingExcessProduct, setPendingExcessProduct] = useState(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [finalizingCount, setFinalizingCount] = useState(false);

  // Sayım başlatma state'leri
  const [showStartModal, setShowStartModal] = useState(false);
  const [countStarted, setCountStarted] = useState(false);
  const [stores, setStores] = useState([]);

  // Geçmiş sayımlar state'leri
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistoryDetailModal, setShowHistoryDetailModal] = useState(false);
  const [selectedHistorySession, setSelectedHistorySession] = useState(null);
  const [loadingHistoryDetail, setLoadingHistoryDetail] = useState(false);

  // Görsel önizleme state'i
  const [previewImage, setPreviewImage] = useState(null);

  // Genel onay modalı (window.confirm yerine)
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    title: '',
    message: '',
    confirmText: 'Evet',
    cancelText: 'İptal',
    type: 'warning',
    onConfirm: () => {}
  });

  // ESC tuşu ile modalları kapatma (öncelik sırası: confirm → detail → history → summary → ana modallar)
  useEscapeClose([
    [showConfirmModal, () => setShowConfirmModal(false)],
    [showHistoryDetailModal, () => setShowHistoryDetailModal(false)],
    [showHistoryModal, () => setShowHistoryModal(false)],
    [showSummaryModal, () => setShowSummaryModal(false)],
    [showExcessModal, () => setShowExcessModal(false)],
    [!!previewImage, () => setPreviewImage(null)],
    [showTerminalModal, () => setShowTerminalModal(false)],
    [showStartModal, () => setShowStartModal(false)],
  ]);

  // Genel onay modal'ı gösterme fonksiyonu
  const showConfirmation = (config) => {
    setConfirmModalConfig({
      title: config.title || 'Onay',
      message: config.message || '',
      confirmText: config.confirmText || 'Evet',
      cancelText: config.cancelText || 'İptal',
      onConfirm: config.onConfirm || (() => {}),
      type: config.type || 'warning'
    });
    setShowConfirmModal(true);
  };

  // Aktif mağaza ID'sini belirle (kasiyer için user.store_id, admin için selectedStore.id)
  const activeStoreId = user?.store_id || selectedStore?.id;

  // Sayım yetkisi kontrolü - Admin veya canInventoryCount yetkisi olan kasiyerler
  const canStartCount = user?.role === 'admin' || user?.permissions?.canInventoryCount === true;

  // Mağazaları yükle
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response = await fetch(`${API_URL}/stores`);
        const data = await response.json();
        setStores(data);
      } catch (error) {
        console.error('Mağaza yükleme hatası:', error);
      }
    };
    fetchStores();
  }, []);

  useEffect(() => {
    if (activeStoreId) {
      // Mağaza değiştiğinde state'leri sıfırla
      setProducts([]);
      setInventoryData({});
      setSession(null);
      setCountStarted(false);
      setLoading(true);
      fetchProducts();
      fetchOrCreateSession();
    } else if (user) {
      // Mağaza seçili değilse loading'i kapat ve uyarı göster
      setLoading(false);
      toast.warning('Sayım için mağaza ataması gerekli!');
    }
  }, [activeStoreId]);

  useEffect(() => {
    if (!loading && barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  }, [loading]);

  // Sayim verisini sunucuya senkronize et (sadece manuel güncellemelerde)
  useEffect(() => {
    // SSE'den gelen güncellemeyi tekrar gönderme
    if (isSSEUpdate.current) {
      isSSEUpdate.current = false;
      return;
    }
    if (session?.id && Object.keys(inventoryData).length > 0) {
      const timeout = setTimeout(() => {
        syncInventoryData();
      }, 100); // Hızlı sync
      return () => clearTimeout(timeout);
    }
  }, [inventoryData, session?.id]);

  // SSE ile gerçek zamanlı güncellemeleri dinle
  useEffect(() => {
    if (!session?.id) return;

    const sseUrl = `${API_HOST}/api/inventory-sessions/${session.id}/stream`;
    console.log('SSE bağlantısı başlatılıyor:', sseUrl);
    // EventSource header gönderemediği için oturum token'ı sorgu parametresiyle gider
    const eventSource = new EventSource(withApiToken(sseUrl));

    eventSource.onopen = () => {
      console.log('SSE bağlantısı açıldı, session:', session.id);
    };

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('SSE mesaj alındı:', data);

        if (data.type === 'inventory_update' && data.inventory_data) {
          // SSE'den gelen güncelleme - tekrar sunucuya gönderme
          isSSEUpdate.current = true;
          setInventoryData(prev => {
            const merged = { ...prev };
            Object.entries(data.inventory_data).forEach(([key, value]) => {
              // Daha büyük değeri al (sayım artışlarını kaçırmamak için)
              if (merged[key] === undefined || merged[key] < value) {
                merged[key] = value;
              }
            });
            return merged;
          });
        }
      } catch (error) {
        console.error('SSE mesaj parse hatası:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE bağlantı hatası:', error, 'readyState:', eventSource.readyState);
      // Hata durumunda yeniden bağlanma EventSource otomatik yapar
    };

    // Cleanup - bağlantıyı kapat
    return () => {
      eventSource.close();
      console.log('SSE bağlantısı kapatıldı');
    };
  }, [session?.id]);

  const fetchProducts = async () => {
    if (!activeStoreId) return;
    try {
      // Mağaza bazlı ürünleri getir (mağazanın stoğuyla)
      const response = await fetch(`${API_URL}/products/store/${activeStoreId}`);
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('Hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrCreateSession = async () => {
    try {
      // Aktif oturum var mi kontrol et
      const response = await fetch(`${API_URL}/inventory-sessions/store/${activeStoreId}/active`);
      const existingSession = await response.json();

      if (existingSession) {
        setSession(existingSession);
        setInventoryData(existingSession.inventory_data || {});
      } else {
        // Yeni oturum olustur
        const createResponse = await fetch(`${API_URL}/inventory-sessions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            store_id: activeStoreId,
            created_by: user.id,
            created_by_name: user.name
          })
        });
        const data = await createResponse.json();
        if (data.success) {
          setSession({ id: data.id, store_id: activeStoreId, terminals: [] });
          // Log kaydet
          logService.log('inventory', 'create', `Yeni sayım oturumu başlatıldı`, {
            entity_type: 'inventory_session',
            entity_id: data.id,
            status: 'success',
            details: {
              store_id: activeStoreId,
              created_by: user.name
            }
          });
        }
      }
    } catch (error) {
      console.error('Oturum hatasi:', error);
    }
  };

  const fetchSessionData = async () => {
    if (!session?.id) return;
    try {
      const response = await fetch(`${API_URL}/inventory-sessions/${session.id}`);
      const data = await response.json();
      if (data && data.inventory_data) {
        // Sadece sunucudan gelen farkli veriler varsa guncelle
        const serverData = data.inventory_data;
        setInventoryData(prev => {
          const merged = { ...prev };
          Object.entries(serverData).forEach(([key, value]) => {
            if (merged[key] === undefined || merged[key] < value) {
              merged[key] = value;
            }
          });
          return merged;
        });
      }
    } catch (error) {
      console.error('Veri cekme hatasi:', error);
    }
  };

  const syncInventoryData = async () => {
    if (!session?.id) return;
    try {
      await fetch(`${API_URL}/inventory-sessions/${session.id}/data`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          inventory_data: inventoryData,
          source: 'main' // Ana sayım sayfasından geldiğini belirt
        })
      });
    } catch (error) {
      console.error('Senkronizasyon hatasi:', error);
    }
  };

  const fetchTerminals = async () => {
    console.log('fetchTerminals çağrıldı, activeStoreId:', activeStoreId);

    if (!activeStoreId) {
      console.warn('store_id bulunamadı!');
      return;
    }
    setLoadingTerminals(true);
    try {
      const url = `${API_URL}/users/terminals/${activeStoreId}`;
      console.log('Terminal API URL:', url);

      const response = await fetch(url);
      const data = await response.json();
      console.log('Terminal listesi sonucu:', data);
      setTerminals(data);
    } catch (error) {
      console.error('Terminal listesi hatasi:', error);
    } finally {
      setLoadingTerminals(false);
    }
  };

  // Terminal modal açıkken session'ı düzenli yenile
  useEffect(() => {
    if (!showTerminalModal) return;
    const interval = setInterval(() => {
      fetchOrCreateSession();
    }, 2000); // Her 2 saniyede yenile
    return () => clearInterval(interval);
  }, [showTerminalModal]);

  const addTerminalToSession = async (terminal) => {
    if (!session?.id) return;
    try {
      const response = await fetch(`${API_URL}/inventory-sessions/${session.id}/terminals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          terminal_user_id: terminal.id,
          terminal_user_name: terminal.name
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`${terminal.name} sayima eklendi!`);
        // Log kaydet
        logService.log('inventory', 'update', `Terminal sayıma eklendi: ${terminal.name}`, {
          entity_type: 'inventory_session',
          entity_id: session.id,
          status: 'info',
          details: {
            terminal_id: terminal.id,
            terminal_name: terminal.name,
            session_id: session.id
          }
        });
        // Oturumu yenile
        fetchOrCreateSession();
      }
    } catch (error) {
      console.error('Terminal ekleme hatasi:', error);
      toast.error('Terminal eklenemedi!');
      logService.log('inventory', 'update', `Terminal ekleme hatası: ${terminal.name}`, {
        status: 'error',
        details: { error: error.message }
      });
    }
  };

  const handleBarcodeInput = (e) => {
    if (e.key === 'Enter' && barcodeInput.trim()) {
      const term = barcodeInput.trim();
      const product = products.find(p =>
        p.barcode === term || p.sku?.toLowerCase() === term.toLowerCase()
      );

      if (product) {
        const currentCount = inventoryData[product.id] || 0;
        const excessCount = excessAdditions[product.id] || 0;
        const newCount = currentCount + 1;

        if (newCount > product.stock + excessCount) {
          // Fazla okutuldu - modal göster
          playSound('error');
          setPendingExcessProduct({
            ...product,
            currentCount,
            newCount,
            excessAmount: newCount - product.stock
          });
          setShowExcessModal(true);
          setLastScanned({ name: `${product.name} (${currentCount}/${product.stock})`, error: true, warning: 'FAZLA!' });
          setBarcodeInput('');
        } else {
          playSound('success');
          setInventoryData(prev => ({
            ...prev,
            [product.id]: newCount
          }));
          const extra = excessCount > 0 ? `+${excessCount}` : '';
          setLastScanned({ name: product.name, count: `${newCount}/${product.stock}${extra}` });
          setBarcodeInput('');
          setTimeout(() => setLastScanned(null), 3000);
        }
      } else {
        playSound('error');
        setLastScanned({ name: term, error: true, warning: 'BULUNAMADI!' });
        toast.error(`"${term}" barkodlu ürün sistemde bulunamadı!`);
        setBarcodeInput('');
        setTimeout(() => setLastScanned(null), 4000);
      }
      barcodeInputRef.current?.focus();
    }
  };

  // Fazla ürünü stoğa ekle
  const handleExcessApprove = () => {
    if (!pendingExcessProduct) return;

    const product = pendingExcessProduct;
    setExcessAdditions(prev => ({
      ...prev,
      [product.id]: (prev[product.id] || 0) + 1
    }));
    setInventoryData(prev => ({
      ...prev,
      [product.id]: product.newCount
    }));

    playSound('success');
    toast.success('Fazla ürün stoğa eklenecek olarak işaretlendi');
    setShowExcessModal(false);
    setPendingExcessProduct(null);
    setLastScanned({ name: product.name, count: `${product.newCount}/${product.stock}+${(excessAdditions[product.id] || 0) + 1}` });
    setTimeout(() => setLastScanned(null), 3000);
  };

  // Fazla ürünü reddet
  const handleExcessReject = () => {
    setShowExcessModal(false);
    setPendingExcessProduct(null);
    toast.info('Fazla ürün sayılmadı');
    setTimeout(() => setLastScanned(null), 2000);
  };

  // Sayım özeti hesapla
  const getCountSummary = () => {
    const productsToCount = getFilteredProducts();
    const summary = {
      scanned: [],
      missing: [],
      excess: [],
      matched: [],
      total: productsToCount.length,
      scannedCount: 0,
      missingCount: 0,
      excessCount: 0
    };

    productsToCount.forEach(product => {
      const counted = inventoryData[product.id] || 0;
      const excessAdd = excessAdditions[product.id] || 0;
      const diff = counted - product.stock;

      if (counted > 0) {
        summary.scannedCount++;
        summary.scanned.push({ ...product, counted, diff, excessToAdd: excessAdd });

        if (diff === 0) {
          summary.matched.push({ ...product, counted });
        } else if (diff > 0) {
          summary.excess.push({ ...product, counted, excessAmount: diff, excessToAdd: excessAdd });
          summary.excessCount += diff;
        } else {
          summary.missing.push({ ...product, counted, missingAmount: Math.abs(diff) });
          summary.missingCount += Math.abs(diff);
        }
      } else if (product.stock > 0) {
        summary.missing.push({ ...product, counted: 0, missingAmount: product.stock });
        summary.missingCount += product.stock;
      }
    });

    return summary;
  };

  // Sayımı tamamla
  const finalizeCount = async () => {
    if (!session?.id) return;

    setFinalizingCount(true);
    try {
      const response = await fetch(`${API_URL}/inventory-sessions/${session.id}/finalize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          inventory_data: inventoryData,
          excess_additions: excessAdditions,
          selected_category: selectedCategory,
          selected_metal_type: selectedMetalType
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Sayım tamamlandı! Stoklar güncellendi.');
        // Log kaydet
        const countedProducts = Object.keys(inventoryData).length;
        const totalItems = Object.values(inventoryData).reduce((sum, val) => sum + val, 0);
        logService.log('inventory', 'inventory_count', `Sayım tamamlandı - ${countedProducts} ürün, ${totalItems} adet`, {
          entity_type: 'inventory_session',
          entity_id: session.id,
          status: 'success',
          details: {
            session_id: session.id,
            store_id: activeStoreId,
            counted_products: countedProducts,
            total_items: totalItems,
            excess_additions: Object.keys(excessAdditions).length
          }
        });
        setShowSummaryModal(false);
        setSession(null);
        setInventoryData({});
        setExcessAdditions({});
        setCountStarted(false); // Butonu "Sayımı Başlat" olarak değiştir
        fetchProducts(); // Ürün listesini yenile
        fetchOrCreateSession(); // Yeni session oluştur
      } else {
        toast.error(data.message || 'Sayım tamamlanamadı!');
        logService.log('inventory', 'inventory_count', `Sayım tamamlanamadı`, {
          status: 'error',
          details: { error: data.message }
        });
      }
    } catch (error) {
      console.error('Sayım tamamlama hatası:', error);
      toast.error('Bir hata oluştu!');
      logService.log('inventory', 'inventory_count', `Sayım tamamlama hatası`, {
        status: 'error',
        details: { error: error.message }
      });
    } finally {
      setFinalizingCount(false);
    }
  };

  const handleManualCount = (productId, value) => {
    if (value === '') {
      setInventoryData(prev => {
        const newData = { ...prev };
        delete newData[productId];
        return newData;
      });
    } else {
      setInventoryData(prev => ({
        ...prev,
        [productId]: parseInt(value) || 0
      }));
    }
  };

  const resetInventory = () => {
    if (Object.keys(inventoryData).length > 0) {
      showConfirmation({
        title: 'Sayımı Sıfırla',
        message: 'Sayım sıfırlansın mı? Bu işlem geri alınamaz.',
        confirmText: 'Evet, Sıfırla',
        cancelText: 'İptal',
        type: 'warning',
        onConfirm: async () => {
          setInventoryData({});
          setLastScanned(null);
          // Boş veriyi direkt backend'e gönder (state henüz güncellenmemiş olabilir)
          if (session?.id) {
            try {
              await fetch(`${API_URL}/inventory-sessions/${session.id}/data`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  inventory_data: {},
                  source: 'main'
                })
              });
            } catch (error) {
              console.error('Sıfırlama senkronizasyon hatası:', error);
            }
          }
        }
      });
    }
  };

  // Geçmiş sayımları getir
  const fetchHistory = async () => {
    if (!activeStoreId) return;
    setLoadingHistory(true);
    try {
      const response = await fetch(`${API_URL}/inventory-sessions/store/${activeStoreId}/history?limit=50`);
      const data = await response.json();
      setHistoryList(data);
    } catch (error) {
      console.error('Geçmiş sayımlar yüklenemedi:', error);
      toast.error('Geçmiş sayımlar yüklenemedi!');
    } finally {
      setLoadingHistory(false);
    }
  };

  // Sayım detayını getir
  const fetchHistoryDetail = async (sessionId) => {
    setLoadingHistoryDetail(true);
    try {
      const response = await fetch(`${API_URL}/inventory-sessions/${sessionId}/details`);
      const data = await response.json();
      setSelectedHistorySession(data);
      setShowHistoryDetailModal(true);
    } catch (error) {
      console.error('Sayım detayı yüklenemedi:', error);
      toast.error('Sayım detayı yüklenemedi!');
    } finally {
      setLoadingHistoryDetail(false);
    }
  };

  // Süreyi formatla (saniye -> saat:dakika:saniye)
  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}s ${minutes}dk ${secs}sn`;
    } else if (minutes > 0) {
      return `${minutes}dk ${secs}sn`;
    }
    return `${secs}sn`;
  };

  // Tarihi formatla
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Benzersiz kategorileri al
  const getCategories = () => {
    let filtered = products;
    if (selectedMetalType !== 'all') filtered = filtered.filter(p => p.metal_type === selectedMetalType);
    const categories = [...new Set(filtered.map(p => p.category).filter(Boolean))];
    return categories.sort();
  };

  // Benzersiz metal türlerini al
  const metalTypeLabels = { gold: 'Altın', silver: 'Gümüş', steel: 'Çelik', brands: 'Markalar', aksesuar: 'Aksesuar', sarf: 'Sarf', zippo: 'Zippo' };
  const getMetalTypes = () => {
    const types = [...new Set(products.map(p => p.metal_type).filter(Boolean))];
    return types.sort();
  };

  const getGroupedProducts = () => {
    const grouped = {};
    let filtered = products;

    // Metal tür filtresi
    if (selectedMetalType !== 'all') {
      filtered = filtered.filter(p => p.metal_type === selectedMetalType);
    }

    // Kategori filtresi
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }

    if (filterMode === 'counted') {
      filtered = filtered.filter(p => inventoryData[p.id] !== undefined);
    } else if (filterMode === 'uncounted') {
      filtered = filtered.filter(p => p.stock > 0 && inventoryData[p.id] === undefined);
    } else if (filterMode === 'diff') {
      filtered = filtered.filter(p => {
        const counted = inventoryData[p.id];
        return counted !== undefined && counted !== p.stock;
      });
    }

    filtered.forEach(product => {
      if (!grouped[product.category]) grouped[product.category] = [];
      grouped[product.category].push(product);
    });
    return grouped;
  };

  // Filtrelenmiş ürünleri al (özet için)
  const getFilteredProducts = () => {
    let filtered = products;
    if (selectedMetalType !== 'all') filtered = filtered.filter(p => p.metal_type === selectedMetalType);
    if (selectedCategory !== 'all') filtered = filtered.filter(p => p.category === selectedCategory);
    return filtered;
  };

  const filteredProducts = getFilteredProducts();
  const summary = {
    total: filteredProducts.length,
    counted: 0,
    match: 0,
    excess: 0,
    missing: 0,
    totalStock: 0,      // Toplam stok adedi (sistemde)
    totalScanned: 0     // Toplam okutulan adet
  };

  filteredProducts.forEach(p => {
    summary.totalStock += p.stock || 0;
    const counted = inventoryData[p.id];
    if (counted !== undefined) {
      summary.counted++;
      summary.totalScanned += counted;
      const diff = counted - p.stock;
      if (diff === 0) summary.match++;
      else if (diff > 0) summary.excess++;
      else summary.missing++;
    }
  });

  if (loading) return <div className="inv-loading">Yukleniyor...</div>;

  if (!canStartCount) {
    return (
      <div className="inv-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', flexDirection: 'column', gap: '12px' }}>
        <div style={{ fontSize: '3rem' }}>🔒</div>
        <h2 style={{ color: '#64748b', fontWeight: 600 }}>Sayım Yetkisi Yok</h2>
        <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Bu sayfaya erişmek için sayım yapma yetkinizin olması gerekiyor.</p>
      </div>
    );
  }

  return (
    <div className="inv-container">
      <div className="inv-topbar">
        <div className="inv-barcode">
          <input
            ref={barcodeInputRef}
            type="text"
            placeholder="Barkod / SKU + Enter"
            value={barcodeInput}
            onChange={(e) => setBarcodeInput(e.target.value)}
            onKeyDown={handleBarcodeInput}
          />
          {lastScanned && (
            <span className={`inv-scanned ${lastScanned.error ? 'error' : ''}`}>
              {lastScanned.error
                ? `${lastScanned.warning} ${lastScanned.name}`
                : `+1 ${lastScanned.name} (${lastScanned.count})`}
            </span>
          )}
        </div>
        <div className="inv-stats">
          <span className="stat with-label" title="Sayılan çeşit / Toplam çeşit">
            <span className="stat-label">Çeşit</span>
            {summary.counted}/{summary.total}
          </span>
          <span className="stat stock with-label" title="Okutulan / Toplam stok">
            <span className="stat-label">Stok</span>
            {summary.totalScanned}/{summary.totalStock}
          </span>
          <span className="stat match with-label" title="Tam eşleşen">
            <span className="stat-label">Tam</span>
            {summary.match}
          </span>
          <span className="stat excess with-label" title="Fazla okutulan">
            <span className="stat-label">Fazla</span>
            +{summary.excess}
          </span>
          <span className="stat missing with-label" title="Eksik okutulan">
            <span className="stat-label">Eksik</span>
            -{summary.missing}
          </span>
        </div>
        <div className="inv-actions">
          <button
            className={`terminal-add-btn ${!countStarted ? 'disabled' : ''}`}
            onClick={() => {
              if (!countStarted) {
                toast.warning('Önce "Sayımı Başlat" butonuna tıklayın!');
                return;
              }
              setShowTerminalModal(true);
              fetchTerminals();
              fetchOrCreateSession();
            }}
            disabled={!countStarted}
            title={!countStarted ? 'Önce sayımı başlatın' : 'Terminal ekle'}
          >
            + Terminal
          </button>
          {getMetalTypes().length > 1 && (
            <select
              value={selectedMetalType}
              onChange={(e) => { setSelectedMetalType(e.target.value); setSelectedCategory('all'); }}
              className="category-select"
            >
              <option value="all">Tüm Türler</option>
              {getMetalTypes().map(mt => (
                <option key={mt} value={mt}>{metalTypeLabels[mt] || mt}</option>
              ))}
            </select>
          )}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="category-select"
          >
            <option value="all">Tüm Kategoriler</option>
            {getCategories().map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <select value={filterMode} onChange={(e) => setFilterMode(e.target.value)}>
            <option value="all">Tümü</option>
            <option value="counted">Sayılanlar</option>
            <option value="uncounted">Sayılmayanlar</option>
            <option value="diff">Farklılar</option>
          </select>
          <button
            className="history-btn"
            onClick={() => {
              setShowHistoryModal(true);
              fetchHistory();
            }}
            title="Geçmiş Sayımlar"
          >
            <History size={16} /> Geçmiş
          </button>
          <button onClick={resetInventory}>Sıfırla</button>
          {!countStarted ? (
            canStartCount ? (
              <button
                className="finish-count-btn start"
                onClick={() => setShowStartModal(true)}
              >
                Sayımı Başlat
              </button>
            ) : (
              <button
                className="finish-count-btn start disabled"
                disabled
                title="Sayım yetkisi yok"
              >
                Sayımı Başlat
              </button>
            )
          ) : (
            canStartCount ? (
              <button
                className="finish-count-btn"
                onClick={() => setShowSummaryModal(true)}
              >
                Sayımı Bitir
              </button>
            ) : null
          )}
        </div>
      </div>

      {/* Aktif terminaller */}
      {session?.terminals?.length > 0 && (
        <div className="inv-terminals-bar">
          <span>Sayimda: </span>
          {session.terminals.map(t => (
            <span key={t.id} className={`terminal-tag ${t.status}`}>
              {t.terminal_user_name}
            </span>
          ))}
        </div>
      )}

      <div className="inv-content">
        {Object.entries(getGroupedProducts()).map(([category, items]) => (
          <div key={category} className="inv-category">
            <div className="cat-header">{category} <span>({items.length})</span></div>
            <table>
              <thead>
                <tr>
                  <th className="th-image"></th>
                  <th>Urun</th>
                  <th>Barkod/SKU</th>
                  <th>Sistem</th>
                  <th>Sayim</th>
                  <th>Fark</th>
                </tr>
              </thead>
              <tbody>
                {items.map(p => {
                  const counted = inventoryData[p.id];
                  const hasCounted = counted !== undefined;
                  const diff = hasCounted ? counted - p.stock : null;
                  const cls = !hasCounted ? '' : diff === 0 ? 'match' : diff > 0 ? 'excess' : 'missing';

                  return (
                    <tr key={p.id} className={hasCounted ? 'counted' : ''}>
                      <td className="td-image">
                        {p.image ? (
                          <img
                            src={`${API_URL.replace('/api', '')}${p.image}`}
                            alt={p.name}
                            onClick={() => setPreviewImage({ url: `${API_URL.replace('/api', '')}${p.image}`, name: p.name })}
                          />
                        ) : (
                          <div className="no-image-cell"><Package size={18} /></div>
                        )}
                      </td>
                      <td className="name">{p.name}</td>
                      <td className="code">{p.barcode || p.sku}</td>
                      <td className="num">{p.stock}</td>
                      <td className="num">
                        <input
                          type="number"
                          min="0"
                          value={counted ?? ''}
                          placeholder="-"
                          onChange={(e) => handleManualCount(p.id, e.target.value)}
                        />
                      </td>
                      <td className={`num ${cls}`}>
                        {hasCounted ? (diff > 0 ? `+${diff}` : diff) : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {/* Sayım Başlatma Modal */}
      {showStartModal && (
        <div className="modal-overlay">
          <div className="modal start-count-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Sayım Başlat</h2>
              <button className="close-btn" onClick={() => setShowStartModal(false)}>X</button>
            </div>
            <div className="modal-body">
              <div className="start-count-info">
                <div className="info-row">
                  <span className="info-label">Mağaza:</span>
                  <span className="info-value">
                    {stores.find(s => s.id === activeStoreId)?.name || 'Seçili Mağaza'}
                  </span>
                </div>
                {getMetalTypes().length > 1 && (
                  <div className="info-row">
                    <span className="info-label">Sayılacak Tür:</span>
                    <select
                      value={selectedMetalType}
                      onChange={(e) => { setSelectedMetalType(e.target.value); setSelectedCategory('all'); }}
                      className="category-select-modal"
                    >
                      <option value="all">Tüm Türler ({products.length} adet)</option>
                      {getMetalTypes().map(mt => {
                        const mtCount = products.filter(p => p.metal_type === mt).length;
                        return (
                          <option key={mt} value={mt}>{metalTypeLabels[mt] || mt} ({mtCount} adet)</option>
                        );
                      })}
                    </select>
                  </div>
                )}
                <div className="info-row">
                  <span className="info-label">Sayılacak Kategori:</span>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="category-select-modal"
                  >
                    <option value="all">Tüm Kategoriler ({getFilteredProducts().length} adet)</option>
                    {getCategories().map(cat => {
                      const catCount = getFilteredProducts().filter(p => p.category === cat).length;
                      return (
                        <option key={cat} value={cat}>{cat} ({catCount} adet)</option>
                      );
                    })}
                  </select>
                </div>
                <div className="info-row summary">
                  <span className="info-label">Sayılacak Ürün:</span>
                  <span className="info-value highlight">
                    {getFilteredProducts().length} ürün
                  </span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowStartModal(false)}>İptal</button>
              <button
                className="confirm-btn"
                onClick={async () => {
                  // Sayım verilerini sıfırla
                  setInventoryData({});
                  setExcessAdditions({});
                  setLastScanned(null);
                  setCountStarted(true);
                  setShowStartModal(false);
                  // Sunucudaki session'ı başlat (created_at güncellenir)
                  if (session?.id) {
                    try {
                      await fetch(`${API_URL}/inventory-sessions/${session.id}/start`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' }
                      });
                      // Session'ı yenile (yeni created_at'ı almak için)
                      fetchOrCreateSession();
                    } catch (error) {
                      console.error('Sayım başlatma hatası:', error);
                    }
                    // Log kaydet
                    logService.log('inventory', 'update', `Sayım başlatıldı`, {
                      entity_type: 'inventory_session',
                      entity_id: session.id,
                      status: 'info',
                      details: {
                        category: selectedCategory === 'all' ? 'Tüm ürünler' : selectedCategory
                      }
                    });
                  }
                  toast.success(`Sayım başladı! ${selectedCategory === 'all' ? 'Tüm ürünler' : selectedCategory} sayılacak.`);
                  barcodeInputRef.current?.focus();
                }}
              >
                Sayımı Başlat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Terminal Ekleme Modal */}
      {showTerminalModal && (
        <div className="modal-overlay">
          <div className="modal terminal-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Terminal Ekle</h2>
              <button className="close-btn" onClick={() => setShowTerminalModal(false)}>X</button>
            </div>
            <div className="modal-body">
              {loadingTerminals ? (
                <p>Yukleniyor...</p>
              ) : terminals.length === 0 ? (
                <p className="empty-text">Bu magazada kayitli terminal yok.</p>
              ) : (
                <div className="terminal-list">
                  {terminals.map(terminal => {
                    const terminalEntry = session?.terminals?.find(t => t.terminal_user_id === terminal.id);
                    const isActive = terminalEntry && (terminalEntry.status === 'invited' || terminalEntry.status === 'active');
                    const hasLeft = terminalEntry && terminalEntry.status === 'left';
                    const hasCompleted = terminalEntry && terminalEntry.status === 'completed';
                    return (
                      <div key={terminal.id} className="terminal-item">
                        <div className="terminal-info">
                          <span className="terminal-name">{terminal.name}</span>
                          <span className="terminal-email">{terminal.email}</span>
                          {terminalEntry?.status === 'invited' && <span className="terminal-status invited">Bekliyor</span>}
                          {terminalEntry?.status === 'active' && <span className="terminal-status active">Aktif</span>}
                          {hasLeft && <span className="terminal-status left">Çıktı</span>}
                          {hasCompleted && <span className="terminal-status completed">Tamamladı</span>}
                        </div>
                        <button
                          className="add-terminal-btn"
                          onClick={() => addTerminalToSession(terminal)}
                        >
                          {terminalEntry ? 'Tekrar Davet Et' : 'Davet Et'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fazla Ürün Modal */}
      {showExcessModal && pendingExcessProduct && (
        <div className="modal-overlay">
          <div className="modal excess-modal">
            <div className="modal-header">
              <h2>Fazla Urun Tespit Edildi!</h2>
            </div>
            <div className="modal-body">
              <div className="excess-info">
                <p><strong>{pendingExcessProduct.name}</strong></p>
                <p className="excess-barcode">{pendingExcessProduct.barcode}</p>
                <div className="excess-counts">
                  <span>Sistemde: <strong>{pendingExcessProduct.stock}</strong></span>
                  <span>Sayilan: <strong>{pendingExcessProduct.newCount}</strong></span>
                  <span className="excess-diff">+{pendingExcessProduct.excessAmount} fazla</span>
                </div>
              </div>
              <p className="excess-question">Bu fazla urunu stoga eklemek ister misiniz?</p>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={handleExcessReject}>Hayir</button>
              <button className="confirm-btn" onClick={handleExcessApprove}>Evet, Ekle</button>
            </div>
          </div>
        </div>
      )}

      {/* Sayım Özeti Modal */}
      {showSummaryModal && (
        <div className="modal-overlay">
          <div className="modal summary-modal">
            <div className="modal-header">
              <h2>Sayim Ozeti</h2>
              <button className="close-btn" onClick={() => setShowSummaryModal(false)}>X</button>
            </div>
            <div className="modal-body">
              {(() => {
                const s = getCountSummary();
                return (
                  <>
                    <div className="summary-stats-grid">
                      <div className="summary-stat-box">
                        <span className="stat-num">{s.scannedCount}</span>
                        <span className="stat-lbl">Sayilan</span>
                      </div>
                      <div className="summary-stat-box match">
                        <span className="stat-num">{s.matched.length}</span>
                        <span className="stat-lbl">Eslesen</span>
                      </div>
                      <div className="summary-stat-box excess">
                        <span className="stat-num">{s.excess.length}</span>
                        <span className="stat-lbl">Fazla</span>
                      </div>
                      <div className="summary-stat-box missing">
                        <span className="stat-num">{s.missing.length}</span>
                        <span className="stat-lbl">Eksik</span>
                      </div>
                    </div>

                    <div className="summary-stats-grid summary-stats-grid-secondary">
                      <div className="summary-stat-box">
                        <span className="stat-num">{summary.counted}/{summary.total}</span>
                        <span className="stat-lbl">Sayilan / Toplam Cesit</span>
                      </div>
                      <div className="summary-stat-box">
                        <span className="stat-num">{summary.totalScanned}/{summary.totalStock}</span>
                        <span className="stat-lbl">Sayilan / Toplam Adet</span>
                      </div>
                    </div>

                    {s.missing.length > 0 && (
                      <div className="summary-section missing">
                        <h4>Eksik Urunler ({s.missingCount} adet)</h4>
                        <p className="section-hint">Bu urunler stoktan dusurulecek</p>
                        <div className="summary-items">
                          {s.missing.slice(0, 10).map(item => (
                            <div key={item.id} className="summary-item with-image">
                              <div className="summary-item-image" onClick={() => item.image && setPreviewImage({ url: `${API_URL.replace('/api', '')}${item.image}`, name: item.name })}>
                                {item.image ? (
                                  <img src={`${API_URL.replace('/api', '')}${item.image}`} alt={item.name} />
                                ) : (
                                  <div className="no-image"><Package size={24} /></div>
                                )}
                              </div>
                              <div className="summary-item-info">
                                <span className="item-name">{item.name}</span>
                                <span className="item-barcode">{item.barcode || item.sku || '-'}</span>
                              </div>
                              <span className="diff">-{item.missingAmount}</span>
                            </div>
                          ))}
                          {s.missing.length > 10 && <p className="more">+{s.missing.length - 10} urun daha</p>}
                        </div>
                      </div>
                    )}

                    {s.excess.length > 0 && (
                      <div className="summary-section excess">
                        <h4>Fazla Urunler ({s.excessCount} adet)</h4>
                        <p className="section-hint">Bu urunler stoga eklenecek</p>
                        <div className="summary-items">
                          {s.excess.map(item => (
                            <div key={item.id} className="summary-item with-image">
                              <div className="summary-item-image" onClick={() => item.image && setPreviewImage({ url: `${API_URL.replace('/api', '')}${item.image}`, name: item.name })}>
                                {item.image ? (
                                  <img src={`${API_URL.replace('/api', '')}${item.image}`} alt={item.name} />
                                ) : (
                                  <div className="no-image"><Package size={24} /></div>
                                )}
                              </div>
                              <div className="summary-item-info">
                                <span className="item-name">{item.name}</span>
                                <span className="item-barcode">{item.barcode || item.sku || '-'}</span>
                              </div>
                              <span className="diff">+{item.excessToAdd}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {s.matched.length > 0 && (
                      <div className="summary-section matched">
                        <h4>Tam Eslesen ({s.matched.length} urun)</h4>
                        <div className="summary-items compact">
                          {s.matched.slice(0, 5).map(item => (
                            <div key={item.id} className="summary-item with-image">
                              <div className="summary-item-image" onClick={() => item.image && setPreviewImage({ url: `${API_URL.replace('/api', '')}${item.image}`, name: item.name })}>
                                {item.image ? (
                                  <img src={`${API_URL.replace('/api', '')}${item.image}`} alt={item.name} />
                                ) : (
                                  <div className="no-image"><Package size={24} /></div>
                                )}
                              </div>
                              <div className="summary-item-info">
                                <span className="item-name">{item.name}</span>
                                <span className="item-barcode">{item.barcode || item.sku || '-'}</span>
                              </div>
                              <span className="diff ok">{item.counted}</span>
                            </div>
                          ))}
                          {s.matched.length > 5 && <p className="more">+{s.matched.length - 5} urun daha</p>}
                        </div>
                      </div>
                    )}

                    {s.missingCount > 0 && (
                      <div className="warning-box">
                        Eksik urunler stoktan dusurulecek. Bu islem geri alinamaz!
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowSummaryModal(false)}>Geri</button>
              <button
                className="confirm-btn finalize"
                onClick={finalizeCount}
                disabled={finalizingCount}
              >
                {finalizingCount ? 'Isleniyor...' : 'Onayla ve Stoklari Guncelle'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Genel Onay Modal (window.confirm yerine) */}
      {showConfirmModal && (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
          <div className="modal confirm-modal" onClick={e => e.stopPropagation()} style={{
            maxWidth: '420px',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div className="modal-header" style={{
              background: confirmModalConfig.type === 'danger' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
                         confirmModalConfig.type === 'info' ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' :
                         'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
              padding: '20px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <AlertTriangle size={28} />
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>{confirmModalConfig.title}</h2>
            </div>
            <div className="modal-body" style={{ padding: '24px', background: '#fff' }}>
              <p style={{ margin: 0, fontSize: '15px', color: '#374151', lineHeight: '1.6' }}>
                {confirmModalConfig.message}
              </p>
            </div>
            <div className="modal-footer" style={{
              padding: '16px 24px',
              background: '#f9fafb',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                onClick={() => setShowConfirmModal(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {confirmModalConfig.cancelText}
              </button>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  confirmModalConfig.onConfirm();
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: confirmModalConfig.type === 'danger' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
                             confirmModalConfig.type === 'info' ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' :
                             'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {confirmModalConfig.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Geçmiş Sayımlar Modal */}
      {showHistoryModal && (
        <div className="modal-overlay" onClick={() => setShowHistoryModal(false)}>
          <div className="modal history-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><History size={20} /> Geçmiş Sayımlar</h2>
              <button className="close-btn" onClick={() => setShowHistoryModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {loadingHistory ? (
                <div className="loading-state">Yükleniyor...</div>
              ) : historyList.length === 0 ? (
                <div className="empty-state">
                  <Package size={48} />
                  <p>Henüz tamamlanmış sayım bulunmuyor.</p>
                </div>
              ) : (
                <div className="history-cards-grid">
                  {historyList.map(session => (
                    <div
                      key={session.id}
                      className="history-card"
                      onClick={() => fetchHistoryDetail(session.id)}
                    >
                      <div className="history-card-header">
                        <div className="history-card-date">
                          <Clock size={16} />
                          <span>{formatDate(session.completed_at || session.created_at)}</span>
                        </div>
                        <div className="history-card-duration">
                          {formatDuration(session.duration_seconds)}
                        </div>
                      </div>
                      <div className="history-card-stats">
                        <div className="history-stat total">
                          <span className="stat-value">{session.total_products}</span>
                          <span className="stat-label">Toplam</span>
                        </div>
                        <div className="history-stat match">
                          <CheckCircle size={16} />
                          <span className="stat-value">{session.matched_count}</span>
                          <span className="stat-label">Tam</span>
                        </div>
                        <div className="history-stat missing">
                          <XCircle size={16} />
                          <span className="stat-value">{session.missing_count}</span>
                          <span className="stat-label">Eksik</span>
                        </div>
                        <div className="history-stat excess">
                          <AlertCircle size={16} />
                          <span className="stat-value">{session.excess_count}</span>
                          <span className="stat-label">Fazla</span>
                        </div>
                      </div>
                      <div className="history-card-footer">
                        <span className="history-user">
                          <Users size={12} /> {session.created_by_name}
                        </span>
                        <span className="history-view-btn">Detayları Gör →</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sayım Detay Modal */}
      {showHistoryDetailModal && selectedHistorySession && (
        <div className="modal-overlay" onClick={() => setShowHistoryDetailModal(false)}>
          <div className="modal history-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Sayım Detayı</h2>
              <button className="close-btn" onClick={() => setShowHistoryDetailModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {loadingHistoryDetail ? (
                <div className="loading-state">Yükleniyor...</div>
              ) : (
                <>
                  {/* Genel Bilgiler */}
                  <div className="detail-section info-section">
                    <h3>Genel Bilgiler</h3>
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="info-label">Mağaza</span>
                        <span className="info-value">{selectedHistorySession.store_name}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Başlangıç</span>
                        <span className="info-value">{formatDate(selectedHistorySession.created_at)}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Bitiş</span>
                        <span className="info-value">{formatDate(selectedHistorySession.completed_at)}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Süre</span>
                        <span className="info-value">{formatDuration(selectedHistorySession.duration_seconds)}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Oluşturan</span>
                        <span className="info-value">{selectedHistorySession.created_by_name}</span>
                      </div>
                    </div>
                  </div>

                  {/* Özet İstatistikler */}
                  <div className="detail-section stats-section">
                    <h3>Sayım Özeti</h3>
                    <div className="stats-grid">
                      <div className="stat-box total">
                        <span className="stat-num">{selectedHistorySession.total_products}</span>
                        <span className="stat-label">Toplam Ürün</span>
                      </div>
                      <div className="stat-box match">
                        <CheckCircle size={20} />
                        <span className="stat-num">{selectedHistorySession.matched_count}</span>
                        <span className="stat-label">Tam Eşleşen</span>
                      </div>
                      <div className="stat-box missing">
                        <XCircle size={20} />
                        <span className="stat-num">{selectedHistorySession.missing_count}</span>
                        <span className="stat-label">Eksik</span>
                      </div>
                      <div className="stat-box excess">
                        <AlertCircle size={20} />
                        <span className="stat-num">{selectedHistorySession.excess_count}</span>
                        <span className="stat-label">Fazla</span>
                      </div>
                    </div>
                  </div>

                  {/* Terminal Bilgileri */}
                  {selectedHistorySession.terminals && selectedHistorySession.terminals.length > 0 && (
                    <div className="detail-section terminals-section">
                      <h3><Users size={16} /> Katılan Terminaller</h3>
                      <div className="terminals-list">
                        {selectedHistorySession.terminals.map((terminal, index) => (
                          <div key={index} className="terminal-item">
                            <span className="terminal-name">{terminal.terminal_user_name}</span>
                            <span className={`terminal-status ${terminal.status}`}>{terminal.status}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Ürün Detayları - Tablo Listesi */}
                  {selectedHistorySession.product_results && selectedHistorySession.product_results.length > 0 ? (
                    <div className="detail-section products-list-section">
                      <h3><Package size={16} /> Sayılan Ürünler ({selectedHistorySession.product_results.length})</h3>
                      <div className="history-products-table-wrapper">
                        <table className="history-products-table">
                          <thead>
                            <tr>
                              <th className="th-img"></th>
                              <th>Ürün / Barkod</th>
                              <th className="th-num">Eski Stok</th>
                              <th className="th-num">Sayılan</th>
                              <th className="th-num">Fark</th>
                              <th className="th-status">Durum</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...selectedHistorySession.product_results]
                              .sort((a, b) => {
                                // Sıralama: 1-Eksik, 2-Fazla, 3-Tam
                                const order = { missing: 1, not_counted_missing: 1, excess: 2, match: 3 };
                                return (order[a.reason] || 4) - (order[b.reason] || 4);
                              })
                              .map((product, index) => {
                              const imgUrl = product.image
                                ? (product.image.startsWith('/uploads/')
                                    ? `${API_URL.replace('/api', '')}${product.image}`
                                    : `${API_URL.replace('/api', '')}/uploads/${product.image}`)
                                : null;
                              return (
                                <tr key={index} className={product.reason}>
                                  <td className="td-img">
                                    <div
                                      className="history-table-img"
                                      onClick={() => imgUrl && setPreviewImage({ url: imgUrl, name: product.product_name })}
                                    >
                                      {imgUrl ? (
                                        <img src={imgUrl} alt={product.product_name} />
                                      ) : (
                                        <div className="no-img"><Package size={18} /></div>
                                      )}
                                    </div>
                                  </td>
                                  <td className="td-info">
                                    <span className="p-name">{product.product_name}</span>
                                    <span className="p-barcode">{product.barcode || '-'}</span>
                                  </td>
                                  <td className="td-num">{product.old_stock}</td>
                                  <td className="td-num">{product.new_stock}</td>
                                  <td className={`td-num td-diff ${product.reason}`}>
                                    {product.reason === 'match' ? '0' :
                                     product.reason === 'excess' ? `+${product.difference}` :
                                     product.difference}
                                  </td>
                                  <td className="td-status">
                                    <span className={`status-tag ${product.reason}`}>
                                      {product.reason === 'match' ? 'Tam' :
                                       product.reason === 'excess' ? 'Fazla' :
                                       product.reason === 'not_counted_missing' ? 'Sayılmadı' : 'Eksik'}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="detail-section">
                      <p className="no-data">Ürün detayı bulunamadı.</p>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="close-detail-btn" onClick={() => setShowHistoryDetailModal(false)}>
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Görsel Önizleme Modal */}
      {previewImage && (
        <div className="image-preview-overlay" onClick={() => setPreviewImage(null)}>
          <div className="image-preview-container" onClick={e => e.stopPropagation()}>
            <button className="image-preview-close" onClick={() => setPreviewImage(null)}>×</button>
            <img src={previewImage.url} alt={previewImage.name} />
            <div className="image-preview-name">{previewImage.name}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryCount;
